import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { artProductId, productLabel } from '../../lib/plan';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { isKitAsset, productDeviceAsset, resolveHeroImage } from './assets';
import { Frame, SubCard, TagPill, focalPointStyle } from './primitives';

/**
 * tactic_moment (design.md V12; slide map "SPLIT dark + overlay card
 * (close-style)"): a full-height image slot to the card edge (kit device
 * fallback via `resolveHeroImage` - never a blank shape, same rule every
 * momentum hero follows) with a dark overlay card lower-left holding the
 * editable headline + body and a lime chip carrying the product name. Reads
 * exactly what momentum_v1's `TacticMomentPage`
 * (web/src/pages/momentum/TacticPages.tsx) reads - same `productId`,
 * `content.headline`/`content.body`, same kit-asset fallback - just the v2
 * frame/primitives instead of v1's Motifs.
 */
export function TacticMomentPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const productId = section.productId;
  const { productName } = useContentLibrary();
  const label = productId ? (productName(productId) ?? productLabel(proposal, productId)) : section.title;
  const imageUrl = section.content.imageUrl;
  const artId = productId ? artProductId(proposal, productId) : productId;
  const heroSrc = resolveHeroImage({ imageUrl, productId: artId, fallback: productDeviceAsset(artId) });
  const usingKitFallback = isKitAsset(heroSrc);

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-moment-root"
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-moment-media">
          <img
            src={heroSrc}
            alt={label}
            className={`mv3-moment-image${usingKitFallback ? ' mv3-hero-contain' : ''}`}
            style={focalPointStyle(section)}
          />
          {!readOnly ? (
            <ImageSlot
              url={imageUrl}
              alt={label}
              readOnly={readOnly}
              onSave={(next) => actions.saveContent(section, { imageUrl: next })}
              className="mv3-moment-image-affordance"
              hideImage
              proposalId={proposal.proposalId}
              defaultQuery={section.title}
            />
          ) : null}
          <SubCard tone="dark" className="mv3-moment-overlay">
            <div className="mv3-moment-copy">
              <EditableText
                value={section.content.headline}
                field="headline"
                placeholder="Set the moment"
                readOnly={readOnly}
                variant="headline"
                clampLines={2}
                onSave={(headline) => actions.saveContent(section, { headline })}
              />
            </div>
            <div className="mv3-moment-copy">
              <EditableText
                value={section.content.body}
                field="body"
                placeholder="Describe when and where the audience meets this channel"
                readOnly={readOnly}
                clampLines={3}
                wordBudget={50}
                onSave={(body) => actions.saveContent(section, { body })}
              />
            </div>
            <TagPill tone="lime" className="mv3-moment-chip">
              {label}
            </TagPill>
          </SubCard>
        </div>
      </Frame>
    </PageShell>
  );
}
